import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { FormTextarea } from '../../../../../components/textarea/index';
import { useLocale } from '../../../../../hooks/index';
import {
  buildStakeholderDocumentBuckets,
  EventDocumentsSection,
} from '../../components/event-documents-section';
import { ChildrenFormProps } from '../../event-form.types';
import { FormSchema, MAX_EVENT_ADDITIONAL_NOTES_LENGTH } from '../../event-form.validation';

export type CompanyIncorporationStepThreeProps = ChildrenFormProps;

export const CompanyIncorporationStepThree: FC<CompanyIncorporationStepThreeProps> = ({
  control,
  setFormData,
  formData,
  handleCloseModal,
  nextFormStep,
  prevFormStep,
  setError,
  clearErrors,
  errors,
  lockMode,
  isLoading,
}) => {
  const { messagesLocale } = useLocale();
  const { companyIncorporation } = useWatch<FormSchema>({ control });
  const issuances = companyIncorporation?.issuances ?? [];

  const stakeholderBuckets = buildStakeholderDocumentBuckets(
    issuances,
    (index) => `companyIncorporation.issuances.${index}.files`,
    messagesLocale['ISSUANCE'],
  );

  // Every bucket, General included, must have settled before the step can advance: a row still
  // uploading would be saved without its document link.
  const allBuckets = [
    companyIncorporation?.files ?? [],
    ...issuances.map(({ files }) => files ?? []),
  ];
  const canProceed = allBuckets.every((files) =>
    files.every((file) => !file.doc || Boolean(file.docLink)),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col gap-6 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <EventDocumentsSection
          clearErrors={clearErrors}
          errors={errors}
          formData={formData}
          generalField="companyIncorporation.files"
          setError={setError}
          setFormData={setFormData}
          stakeholderBuckets={stakeholderBuckets}
        />
        <FormTextarea
          control={control}
          maxLength={MAX_EVENT_ADDITIONAL_NOTES_LENGTH}
          name="companyIncorporation.additionalNotes"
          placeholder={<AppFormattedMessage id="ADDITIONAL_NOTES_OPTIONAL" />}
          styleVariant="DEFAULT_W_PLACEHOLDER"
          wrapperClassName="bg-gray-50"
        />
      </div>
      <div className="flex h-9 w-full shrink-0 justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          {!lockMode && (
            <Button
              className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
              onClick={prevFormStep}
              styleType="NONE"
              type="button"
            >
              <AppFormattedMessage id="BACK" />
            </Button>
          )}
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!canProceed}
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
